import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Coins, Lock, Check, ArrowRight, ShieldCheck, CalendarClock, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMyLevel } from "@/lib/api";
import { BackButton } from "@/components/BackButton";

const fmt = (n) => (n ?? 0).toLocaleString("it-IT");

function ProgressBar({ value, max, label }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 100;
  return (
    <div
      className="h-3 w-full rounded-full bg-surfaceTertiary overflow-hidden border border-hair"
      role="progressbar"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Levels() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "I miei livelli · Readoo";
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    getMyLevel().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (authLoading || !user) return null;

  if (loading || !data) {
    return (
      <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BackButton />
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>
      </div>
    );
  }

  const { current_level, max_level_reached, level_name, article_reward, validated_reading_redoo_lifetime, is_max, next_level, maintenance, levels } = data;
  const m = maintenance || {};

  return (
    <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="levels-page">
      <BackButton />

      {/* Header livello attuale */}
      <div className="rounded-2xl border border-brand/40 bg-brandTertiary/40 p-6 mb-6">
        <p className="text-[11px] font-mono uppercase tracking-wider text-brand mb-1">Il tuo livello</p>
        <div className="flex items-center gap-3">
          <Trophy size={30} className="text-brand shrink-0" />
          <div>
            <h1 data-testid="levels-current-name" className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface leading-none">
              Livello {current_level} — {level_name}
            </h1>
            <p className="text-sm text-onSurfaceSecondary mt-1.5 flex items-center gap-1.5">
              <Coins size={15} className="text-brand" /> Bonus attuale: <b className="text-onSurface">{article_reward} Readoo</b> per ogni articolo letto
            </p>
          </div>
        </div>

        <div className="mt-5">
          {is_max ? (
            <div className="flex items-center gap-2 text-sm font-semibold text-brand">
              <Sparkles size={16} /> Livello massimo raggiunto
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-onSurfaceSecondary">
                  {fmt(validated_reading_redoo_lifetime)} / {fmt(next_level?.min_validated_redoo)} <span className="text-subtle">Readoo convalidati</span>
                </span>
                <span className="text-subtle font-mono text-xs">{fmt(next_level?.remaining)} al Livello {next_level?.level_number}</span>
              </div>
              <ProgressBar value={validated_reading_redoo_lifetime} max={next_level?.min_validated_redoo || 1} label="Progressione verso il prossimo livello" />
            </>
          )}
        </div>
      </div>

      {/* Prossimo livello */}
      {next_level && (
        <div className="rounded-2xl border border-hair bg-surfaceSecondary p-5 mb-6" data-testid="levels-next">
          <p className="text-[11px] font-mono uppercase tracking-wider text-subtle mb-2">Prossimo livello</p>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-serif text-xl font-semibold text-onSurface">Livello {next_level.level_number} — {next_level.name}</p>
              <p className="text-sm text-onSurfaceSecondary mt-0.5">Da {fmt(next_level.min_validated_redoo)} Readoo convalidati · <b className="text-onSurface">{next_level.article_reward} Readoo</b>/articolo</p>
            </div>
            <div className="text-right">
              <p className="font-serif text-2xl font-bold text-brand">{fmt(next_level.remaining)}</p>
              <p className="text-[11px] text-subtle">Readoo convalidati mancanti</p>
            </div>
          </div>
        </div>
      )}

      {/* Mantenimento */}
      {current_level > 1 && (
        <div className="rounded-2xl border border-hair bg-surfaceSecondary p-5 mb-6" data-testid="levels-maintenance">
          <p className="text-[11px] font-mono uppercase tracking-wider text-subtle mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> Mantieni il Livello {current_level}</p>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-onSurfaceSecondary">{m.active_days} / {m.required_active_days} giorni attivi</span>
            {m.remaining_days_in_cycle != null && <span className="text-subtle font-mono text-xs">{m.remaining_days_in_cycle} giorni al controllo</span>}
          </div>
          <ProgressBar value={m.active_days} max={m.required_active_days || 1} label="Attività del periodo di mantenimento" />
          {m.safe ? (
            <p className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-ok"><ShieldCheck size={15} /> Livello al sicuro per questo periodo</p>
          ) : (
            <p className="mt-3 text-sm text-onSurfaceSecondary">
              Ti servono ancora <b className="text-onSurface">{Math.max(0, (m.required_active_days || 0) - (m.active_days || 0))} giorni attivi</b> per mantenere il livello.
            </p>
          )}
        </div>
      )}

      {/* Elenco livelli */}
      <div className="space-y-2.5 mb-6">
        {levels.map((l) => {
          const state = l.level_number < current_level ? "done" : l.level_number === current_level ? "current" : "locked";
          const unlocked = l.level_number <= max_level_reached;
          return (
            <div
              key={l.level_number}
              data-testid={`level-row-${l.level_number}`}
              className={`flex items-center gap-4 rounded-xl border p-4 ${state === "current" ? "border-brand bg-brandTertiary/30" : "border-hair bg-surfaceSecondary"}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${state === "done" ? "bg-ok/15 text-ok" : state === "current" ? "bg-brand text-onBrand" : "bg-surfaceTertiary text-subtle"}`}>
                {state === "done" ? <Check size={17} /> : state === "current" ? <ArrowRight size={17} /> : <Lock size={15} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg font-semibold text-onSurface leading-tight">
                  Livello {l.level_number} — {l.name}
                  {state === "current" && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-brand">Attuale</span>}
                  {state === "locked" && unlocked && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-subtle">Da recuperare</span>}
                </p>
                <p className="text-xs text-subtle mt-0.5">
                  Da {fmt(l.min_validated_redoo)} Readoo convalidati · {l.article_reward} Readoo/articolo
                  {l.maintenance_active_days > 0 ? ` · mantieni con ${l.maintenance_active_days} gg attivi/30` : " · nessun mantenimento"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Copy esplicativa */}
      <div className="rounded-2xl border border-hair bg-surfaceSecondary/60 p-5 text-sm text-onSurfaceSecondary space-y-2">
        <p>Più leggi e <b className="text-onSurface">convalidi</b> i tuoi Readoo, più sali di livello. Ogni livello aumenta i Readoo che guadagni leggendo gli articoli.</p>
        <p className="text-subtle text-xs">Tutti i Readoo che <b>convalidi</b> (lettura, missioni, streak e bonus) contribuiscono al livello. I Readoo non convalidati entro mezzanotte vengono persi e non contano per la progressione. Per mantenere i livelli più alti resta attivo: conta un giorno attivo ogni volta che leggi almeno un articolo valido.</p>
      </div>
    </div>
  );
}
